import React, { useState, useEffect } from 'react';
import Video, { LocalVideoTrack, RemoteParticipant, Room } from 'twilio-video';
import { Mic, MicOff, Video as VideoIcon, VideoOff, Phone } from 'lucide-react';

interface VideoRoomProps {
  token: string;
  roomName: string;
  handleLogout: () => void;
}

const VideoRoom: React.FC<VideoRoomProps> = ({ token, roomName, handleLogout }) => {
  const [room, setRoom] = useState<Room | null>(null);
  const [participants, setParticipants] = useState<RemoteParticipant[]>([]);
  const [isAudioEnabled, setIsAudioEnabled] = useState(true);
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);
  useEffect(() => {
    const participantConnected = (participant: RemoteParticipant) => {
      setParticipants(prevParticipants => [...prevParticipants, participant]);
    };

    const participantDisconnected = (participant: RemoteParticipant) => {
      setParticipants(prevParticipants =>
        prevParticipants.filter(p => p !== participant)
      );
    };

    Video.connect(token, {
      name: roomName,
      audio: true,
      video: true
    }).then(room => {
      setRoom(room);
      room.on('participantConnected', participantConnected);
      room.on('participantDisconnected', participantDisconnected);
      room.participants.forEach(participantConnected);
    });

    return () => {
      setRoom((currentRoom) => {
        if (currentRoom && currentRoom.localParticipant.state === 'connected') {
          currentRoom.disconnect();
          return null;
        } else {
          return currentRoom;
        }
      });
    };
  }, [roomName, token]);

  const toggleAudio = () => {
    room?.localParticipant.audioTracks.forEach(track => {
      if (track.track.isEnabled) {
        track.track.disable();
      } else {
        track.track.enable();
      }
    });
    setIsAudioEnabled(!isAudioEnabled);
  };

  const toggleVideo = () => {
    room?.localParticipant.videoTracks.forEach(track => {
      if (track.track.isEnabled) {
        track.track.disable();
      } else {
        track.track.enable();
      }
    });
    setIsVideoEnabled(!isVideoEnabled);
  };

  return (
    <div className="flex flex-col h-screen bg-gray-900">
      <div className="flex-1 flex flex-wrap gap-4 p-4">
        {/* Local participant's video */}
        <div className="relative w-full md:w-1/2 lg:w-1/3 aspect-video bg-gray-800 rounded-lg overflow-hidden">
          <div id="local-participant" className="absolute inset-0"></div>
        </div>

        {/* Remote participants' videos */}
        {participants.map(participant => (
          <div 
            key={participant.sid}
            className="relative w-full md:w-1/2 lg:w-1/3 aspect-video bg-gray-800 rounded-lg overflow-hidden"
          >
            <div id={participant.sid} className="absolute inset-0"></div>
          </div>
        ))}
      </div>

      {/* Controls */}
      <div className="flex justify-center gap-4 p-4 bg-gray-800">
        <button
          onClick={toggleAudio}
          className={`p-4 rounded-full ${
            isAudioEnabled ? 'bg-gray-600' : 'bg-red-600'
          }`}
        >
          {isAudioEnabled ? <Mic /> : <MicOff />}
        </button>
        <button
          onClick={toggleVideo}
          className={`p-4 rounded-full ${
            isVideoEnabled ? 'bg-gray-600' : 'bg-red-600'
          }`}
        >
          {isVideoEnabled ? <VideoIcon /> : <VideoOff />}
        </button>
        <button
          onClick={handleLogout}
          className="p-4 rounded-full bg-red-600"
        >
          <Phone />
        </button>
      </div>
    </div>
  );
};

export default VideoRoom;
